import type maplibregl from "maplibre-gl";

/** Resolves once MapLibre has a loaded style (safe to add sources/layers). */
export function waitMapStyleReady(map: maplibregl.Map): Promise<void> {
  if (mapStyleReady(map)) return Promise.resolve();
  return new Promise((resolve) => {
    const finish = (): void => {
      if (!mapStyleReady(map)) return;
      map.off("idle", finish);
      map.off("styledata", finish);
      resolve();
    };
    map.on("idle", finish);
    map.on("styledata", finish);
  });
}

/** Wait for the next idle pass (tile/style work drained). Do not skip when already idle. */
export function waitMapIdle(map: maplibregl.Map): Promise<void> {
  return new Promise((resolve) => {
    map.once("idle", () => resolve());
  });
}

/** True when MapLibre has a live style object (not mid swap / teardown). */
export function mapStyleReady(map: maplibregl.Map): boolean {
  try {
    return map.getStyle() != null && map.isStyleLoaded() === true;
  } catch {
    return false;
  }
}

/** True when the map has a style object (overlay layers may be added while tiles are still loading). */
export function mapHasStyleObject(map: maplibregl.Map): boolean {
  try {
    return map.getStyle() != null;
  } catch {
    return false;
  }
}

/** Run once a style object exists; waits for idle if the style was torn down mid-swap. */
export function whenMapHasStyle(map: maplibregl.Map, fn: () => void): void {
  if (mapHasStyleObject(map)) {
    fn();
    return;
  }
  map.once("idle", () => {
    if (mapHasStyleObject(map)) fn();
  });
}

/** Safe layer lookup — `getLayer` throws when the internal style is undefined. */
export function mapHasLayer(map: maplibregl.Map, layerId: string): boolean {
  if (!mapHasStyleObject(map)) return false;
  try {
    return map.getLayer(layerId) != null;
  } catch {
    return false;
  }
}
